import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Button, Space, Tag, Card, Tabs, Spin, Empty, Modal,
  Form, Input, Select, InputNumber, Typography, Tooltip, message,
  DatePicker, Row, Col, Statistic, Popconfirm, AutoComplete, Switch, Dropdown,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, AccountBookOutlined,
  EditOutlined, DeleteOutlined, CheckOutlined,
  ArrowUpOutlined, ArrowDownOutlined, BankOutlined, TagOutlined,
  DollarOutlined, PrinterOutlined, SearchOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {
  getDailyExpensesApi, createDailyExpenseApi, updateDailyExpenseApi, deleteDailyExpenseApi,
  getDailyExpenseCategoriesApi, createDailyExpenseCategoryApi,
  updateDailyExpenseCategoryApi, deleteDailyExpenseCategoryApi, getIncomeSourcesApi,
  confirmDailyExpenseApi,
} from '../../api/dailyExpense';
import { getCropListFilterApi } from '../../api/crops';
import { getAccountsApi, createAccountApi } from '../../api/accounts';
import { encryptData, decryptData } from '../../utils/crypto';
import { formatCurrency, formatDate } from '../../utils/formatters';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PRIMARY = '#1a6b2a';
const apiErr = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

// ─── Category Modal ───────────────────────────────────────────────────────────
function CategoryModal({ open, onClose, onSuccess, editRecord }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEdit = !!editRecord;

  useEffect(() => {
    if (open) {
      if (isEdit) {
        form.setFieldsValue({
          name: editRecord.name,
          icon: editRecord.icon || '',
          price: editRecord.price ? Number(editRecord.price) : undefined,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, isEdit, editRecord, form]);

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setLoading(true);
    try {
      let newId = editRecord?.id;
      if (isEdit) {
        await updateDailyExpenseCategoryApi(editRecord.id, values);
        message.success('Category updated!');
      } else {
        const res = await createDailyExpenseCategoryApi(values);
        newId = res.data?.id;
        message.success('Category created!');
      }
      onSuccess(newId);
      onClose();
    } catch (err) {
      message.error(apiErr(err, 'Failed to save category'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <TagOutlined />
          {isEdit ? 'Edit Category' : 'Add Category'}
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" loading={loading} onClick={handleSave}
          style={{ background: PRIMARY, borderColor: PRIMARY }}>
          {isEdit ? 'Update' : 'Create'}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical" size="large" style={{ marginTop: 16 }}>
        <Form.Item label="Category Name" name="name" rules={[{ required: true, message: 'Enter category name' }]}>
          <Input placeholder="e.g. Food, Transport, Medicine" />
        </Form.Item>
        <Form.Item
          label="Icon (optional)"
          name="icon"
          extra="Paste an emoji to use as the category icon"
        >
          <Input placeholder="e.g. 🍎 🚗 💊 🌾" maxLength={4} style={{ width: 120 }} />
        </Form.Item>
        <Form.Item
          label="Default Price (optional)"
          name="price"
          extra="Auto-fills the amount when this category is selected"
        >
          <InputNumber
            placeholder="0.00"
            style={{ width: '100%' }}
            prefix="₹"
            min={0}
            precision={2}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Quick Add Bank Account Modal (for inline "+ Add New Bank Account") ───────
function QuickAddBankAccountModal({ open, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setLoading(true);
    try {
      const { name, bank_name, account_holder_name, account_number, ifsc_code, opening_balance, note } = values;
      const payload = {
        name,
        type: 'bank',
        opening_balance,
        bank_details: encryptData({ bank_name, account_holder_name, account_number, ifsc_code }),
        note,
      };
      const res = await createAccountApi(payload);
      message.success('Bank account added!');
      onSuccess(res.data?.id);
      onClose();
    } catch (err) {
      message.error(apiErr(err, 'Failed to add bank account'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<Space><BankOutlined />Add New Bank Account</Space>}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" loading={loading} onClick={handleSave}
          style={{ background: PRIMARY, borderColor: PRIMARY }}>
          Add
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical" size="large" style={{ marginTop: 16 }}>
        <Form.Item label="Account Nick Name" name="name" rules={[{ required: true, message: 'Enter a nick name' }]}>
          <Input placeholder="e.g. SBI Savings" />
        </Form.Item>
        <Form.Item label="Opening Balance (₹)" name="opening_balance" rules={[{ required: true, message: 'Enter opening balance' }]}>
          <InputNumber placeholder="0" style={{ width: '100%' }} min={0} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Bank Name" name="bank_name" rules={[{ required: true, message: 'Enter bank name' }]}>
              <Input placeholder="e.g. State Bank of India" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Account Holder Name" name="account_holder_name" rules={[{ required: true, message: 'Enter account holder name' }]}>
              <Input placeholder="Full name" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Account Number" name="account_number" rules={[{ required: true, message: 'Enter account number' }]}>
              <Input placeholder="Account number" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="IFSC Code" name="ifsc_code" rules={[{ required: true, message: 'Enter IFSC code' }]} normalize={(v) => v?.toUpperCase()}>
              <Input placeholder="e.g. SBIN0001234" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Note (optional)" name="note">
          <Input placeholder="Any note about this account" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Add/Edit Expense Modal ───────────────────────────────────────────────────
function ExpenseFormModal({ open, onClose, onSuccess, editRecord, categories, bankAccounts, incomeSources, onCategoryAdded, onBankAccountAdded }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEdit = !!editRecord;

  const [crops, setCrops] = useState([]);
  const [loadingCrops, setLoadingCrops] = useState(false);

  const [titleOptions, setTitleOptions] = useState([]);
  const titleDebounceRef = useRef(null);

  // Inline "+ Add New" quick-create modals
  const [quickCategoryOpen, setQuickCategoryOpen] = useState(false);
  const [quickBankOpen, setQuickBankOpen] = useState(false);
  const ADD_CATEGORY_OPTION = '__add_category__';
  const ADD_BANK_OPTION = '__add_bank__';

  const handleTitleSearch = (val) => {
    if (titleDebounceRef.current) clearTimeout(titleDebounceRef.current);
    if (!val?.trim()) { setTitleOptions([]); return; }
    titleDebounceRef.current = setTimeout(async () => {
      try {
        const type = form.getFieldValue('type');
        const params = { title: val.trim(), per_page: 20 };
        if (type && type !== 'all') params.type = type;
        const res = await getDailyExpensesApi(params);
        const data = res.data?.data || [];
        const unique = [...new Set(data.map((e) => e.title).filter(Boolean))];
        setTitleOptions(unique.map((t) => ({ value: t })));
      } catch {
        setTitleOptions([]);
      }
    }, 300);
  };

  useEffect(() => {
    if (!open) {
      setCrops([]);
      setTitleOptions([]);
      return;
    }
    const currentYear = dayjs().year();
    const years = [String(currentYear), String(currentYear - 1)];
    const lang = localStorage.getItem('i18nextLng') || 'hi';
    setLoadingCrops(true);
    getCropListFilterApi({ years, lang, per_page: 200 })
      .then((res) => setCrops(res.data?.data || []))
      .catch(() => { })
      .finally(() => setLoadingCrops(false));
  }, [open]);

  const watchedCatId = Form.useWatch('category_id', form);
  const watchedQty = Form.useWatch('quantity', form);
  const watchedCropId = Form.useWatch('crop_id', form);
  const watchedType = Form.useWatch('type', form);
  const watchedIncomeId = Form.useWatch('income_id', form);
  const watchedBankAccountId = Form.useWatch('bank_account_id', form);
  const watchedAmount = Form.useWatch('amount', form);

  const selectedCat = categories.find((c) => c.id === watchedCatId);
  const catPrice = selectedCat?.price ? Number(selectedCat.price) : null;

  const selectedCrop = crops.find((c) => c.id === watchedCropId);
  const cropProfit = selectedCrop ? Number(selectedCrop.vechan || 0) - Number(selectedCrop.kharch || 0) : null;
  const cropSpent = selectedCrop ? Number(selectedCrop.spend_amount || 0) : 0;
  const cropRemaining = cropProfit !== null ? cropProfit - cropSpent : null;

  // Remaining balance of the income this expense is being spent from.
  // When editing an entry already linked to this income, its own amount was
  // already deducted server-side, so add it back for an accurate preview.
  const selectedIncome = incomeSources.find((i) => i.id === watchedIncomeId);
  const incomeRemaining = selectedIncome
    ? Number(selectedIncome.remaining) + (isEdit && editRecord?.income_id === selectedIncome.id ? Number(editRecord.amount) : 0)
    : null;
  // Live preview of what the income balance will be once this expense's
  // amount is deducted — updates as the user types, before they submit.
  const incomeRemainingAfter = incomeRemaining !== null
    ? incomeRemaining - (Number(watchedAmount) || 0)
    : null;

  // Planned entries are only ever set on create — a normal edit can't flip
  // this (see backend). Editing a planned entry keeps it planned; the
  // "Mark as Done" action in the table is the only way to confirm it.
  const isPlannedRecord = isEdit && !!editRecord?.is_planned;

  // Auto-calc amount = price × quantity whenever either changes
  useEffect(() => {
    if (!catPrice) return;
    const qty = Number(watchedQty) || 1;
    form.setFieldValue('amount', parseFloat((catPrice * qty).toFixed(2)));
  }, [watchedQty, catPrice, form]);

  useEffect(() => {
    if (open) {
      if (isEdit) {
        form.setFieldsValue({
          type: editRecord.type || 'expense',
          category_id: editRecord.category_id,
          title: editRecord.title,
          amount: Number(editRecord.amount),
          quantity: editRecord.quantity ? Number(editRecord.quantity) : 1,
          date: editRecord.date ? dayjs(editRecord.date) : dayjs(),
          // daily_expenses.bank_account_id is a string column ('cash' or a stringified
          // accounts.id), but the Select's option values use the numeric account id
          // (see options below) — without normalizing, the type mismatch ("14" !== 14)
          // means no option matches and the Select falls back to showing the raw id
          // instead of the resolved bank name.
          bank_account_id: !editRecord.bank_account_id
            ? undefined
            : editRecord.bank_account_id === 'cash'
              ? 'cash'
              : Number(editRecord.bank_account_id),
          crop_id: editRecord.crop_id || undefined,
          income_id: editRecord.income_id || undefined,
          note: editRecord.note || '',
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ date: dayjs(), type: 'expense', quantity: 1, is_planned: false });
      }
    }
  }, [open, isEdit, editRecord, form]);

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setLoading(true);
    try {
      const cat = categories.find((c) => c.id === values.category_id);
      const payload = {
        ...values,
        title: values.title?.trim() || cat?.name || '',
        date: values.date ? values.date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      };
      delete payload.quantity;
      if (!payload.crop_id) delete payload.crop_id;
      if (!payload.note) delete payload.note;
      if (payload.type !== 'expense' || !payload.income_id) delete payload.income_id;
      if (isEdit) delete payload.is_planned; // only settable on create; "Mark as Done" confirms it later

      if (isEdit) {
        await updateDailyExpenseApi(editRecord.id, payload);
        message.success('Entry updated!');
      } else {
        await createDailyExpenseApi(payload);
        message.success('Entry added!');
      }

      onSuccess();
      if (isEdit) {
        onClose();
      } else {
        form.resetFields();
        form.setFieldsValue({ date: dayjs(), type: 'expense', quantity: 1, is_planned: false });
      }
    } catch (err) {
      message.error(apiErr(err, 'Failed to save entry'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          {isEdit ? <EditOutlined /> : <PlusOutlined />}
          {isEdit ? 'Edit Entry' : 'Add Expense / Income'}
        </Space>
      }
      open={open}
      onCancel={onClose}
      centered
      zIndex={1100}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" loading={loading} onClick={handleSave}
          style={{ background: PRIMARY, borderColor: PRIMARY }}>
          {isEdit ? 'Update' : 'Add'}
        </Button>,
      ]}
      width={680}
      destroyOnClose
    >
      <Form form={form} layout="vertical" size="large" style={{ marginTop: 16 }}>
        {/* Row 1: Date | Type */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Date" name="date" rules={[{ required: true, message: 'Select date' }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Type" name="type" rules={[{ required: true }]}>
              <Select
                onChange={(val) => {
                  if (val !== 'expense') form.setFieldValue('income_id', undefined);
                }}
                options={[
                  { value: 'expense', label: <Space><ArrowDownOutlined style={{ color: '#ff4d4f' }} /><span>Expense</span></Space> },
                  { value: 'income', label: <Space><ArrowUpOutlined style={{ color: '#52c41a' }} /><span>Income</span></Space> },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Planned (future) entry toggle — create only.
            Switch must be Form.Item's direct child for value/onChange
            wiring to work, so the label/description live outside it. */}
        {!isEdit && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20,
            padding: '10px 14px', borderRadius: 8, background: '#fafafa', border: '1px solid #f0f0f0',
          }}>
            <Form.Item name="is_planned" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              <ClockCircleOutlined style={{ marginRight: 6, color: '#faad14' }} />
              Planned for later (hasn't happened yet)
            </div>
          </div>
        )}
        {isPlannedRecord && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 14px', borderRadius: 8, background: '#fffbe6', border: '1px solid #ffe58f',
            marginBottom: 20, fontSize: 12, color: '#874d00',
          }}>
            <ClockCircleOutlined />
            This is a planned entry — use "Mark as Done" in the list to confirm it once it actually happens.
          </div>
        )}

        {/* Row 2: Category | Title */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Category" name="category_id" rules={[{ required: true, message: 'Select a category' }]}>
              <Select
                placeholder="Select category"
                showSearch
                filterOption={(input, opt) =>
                  opt?.value === ADD_CATEGORY_OPTION || (opt?.searchLabel || '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={(catId) => {
                  if (catId === ADD_CATEGORY_OPTION) {
                    form.setFieldValue('category_id', watchedCatId);
                    setQuickCategoryOpen(true);
                    return;
                  }
                  const cat = categories.find((c) => c.id === catId);
                  form.setFieldValue('quantity', 1);
                  if (cat?.price) form.setFieldValue('amount', Number(cat.price));
                }}
                options={[
                  ...categories.map((c) => ({
                    value: c.id,
                    label: c.icon ? `${c.icon} ${c.name}` : c.name,
                    searchLabel: c.name,
                  })),
                  {
                    value: ADD_CATEGORY_OPTION,
                    label: <span style={{ color: PRIMARY, fontWeight: 600 }}><PlusOutlined /> Add New Category</span>,
                    searchLabel: 'add new category',
                  },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Title (optional)" name="title">
              <AutoComplete
                options={titleOptions}
                onSearch={handleTitleSearch}
                placeholder="e.g. Grocery, Salary, Diesel"
                allowClear
                filterOption={(input, option) =>
                  (option?.value || '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Crop (optional) — temporarily disabled */}
        {/* <Form.Item label="Crop (optional)" name="crop_id">
          <Select
            placeholder="Select crop"
            allowClear
            showSearch
            loading={loadingCrops}
            filterOption={(input, opt) =>
              (opt?.searchLabel || '').toLowerCase().includes(input.toLowerCase())
            }
            options={[
              ...([String(dayjs().year()), String(dayjs().year() - 1)].map((yr) => ({
                label: <span style={{ fontWeight: 600, color: '#8c8c8c', fontSize: 12 }}>{yr}</span>,
                title: yr,
                options: crops
                  .filter((c) => {
                    const d = c.crop_date || c.created_at || '';
                    return d.startsWith(yr) && (Number(c.vechan || 0) - Number(c.kharch || 0)) > 0;
                  })
                  .map((c) => ({
                    value: c.id,
                    label: `🌱 ${c.crop_name || c.crop || ''}${c.verity ? ` · ${c.verity}` : ''}${c.farm_name ? ` — ${c.farm_name}` : ''}${c.plot_name ? ` / ${c.plot_name}` : ''}`,
                    searchLabel: `${c.crop_name || c.crop || ''} ${c.verity || ''} ${c.farm_name || ''} ${c.plot_name || ''}`,
                  })),
              }))).filter((g) => g.options.length > 0),
            ]}
          />
        </Form.Item> */}

        {/* Spent From Income — only for expense entries */}
        {watchedType === 'expense' && incomeSources.length > 0 && (
          <Form.Item
            label="Spent From Income (optional)"
            name="income_id"
            extra={selectedIncome ? (
              <span style={{ color: incomeRemaining <= 0 ? '#ff4d4f' : '#52c41a', fontSize: 12 }}>
                Remaining: ₹{incomeRemaining.toLocaleString('en-IN')} of ₹{Number(selectedIncome.amount).toLocaleString('en-IN')}
              </span>
            ) : 'Track which income this expense was paid out of'}
          >
            <Select
              placeholder="Select source income"
              allowClear
              showSearch
              filterOption={(input, opt) => (opt?.searchLabel || '').toLowerCase().includes(input.toLowerCase())}
              options={incomeSources.map((i) => ({
                value: i.id,
                label: `${i.is_planned ? '🕒 ' : ''}${i.title || 'Income'} — ₹${Number(i.remaining).toLocaleString('en-IN')} left of ₹${Number(i.amount).toLocaleString('en-IN')}${i.is_planned ? ' (upcoming)' : ''}`,
                searchLabel: i.title || '',
              }))}
            />
          </Form.Item>
        )}

        {/* Quantity row — only when category has a default price */}
        {catPrice && (
          <Form.Item label="Quantity" name="quantity">
            <InputNumber
              min={1}
              precision={0}
              style={{ width: '100%' }}
              addonAfter={
                <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                  × ₹{Number(catPrice).toLocaleString('en-IN')}
                </span>
              }
            />
          </Form.Item>
        )}

        {/* Row 3: Amount | Bank */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Amount (₹)"
              name="amount"
              rules={[
                { required: true, message: 'Enter amount' },
                { type: 'number', min: 0.01, message: 'Must be > 0' },
                ...(cropRemaining !== null && !isEdit ? [{
                  validator: (_, val) =>
                    val && Number(val) > cropRemaining
                      ? Promise.reject(`Exceeds remaining profit ₹${cropRemaining.toLocaleString('en-IN')}`)
                      : Promise.resolve(),
                }] : []),
                ...(incomeRemaining !== null ? [{
                  validator: (_, val) =>
                    val && Number(val) > incomeRemaining
                      ? Promise.reject(`Exceeds remaining income balance ₹${incomeRemaining.toLocaleString('en-IN')}`)
                      : Promise.resolve(),
                }] : []),
              ]}
              extra={(
                <span>
                  {cropRemaining !== null && !isEdit && (
                    <span style={{ color: cropRemaining <= 0 ? '#ff4d4f' : '#52c41a', fontSize: 12, display: 'block' }}>
                      Remaining profit: ₹{cropRemaining.toLocaleString('en-IN')}
                    </span>
                  )}
                  {selectedIncome && incomeRemainingAfter !== null && Number(watchedAmount) > 0 && (
                    <span style={{ color: incomeRemainingAfter < 0 ? '#ff4d4f' : '#52c41a', fontSize: 12, display: 'block' }}>
                      Remaining: ₹{incomeRemainingAfter.toLocaleString('en-IN')} after this expense (current ₹{incomeRemaining.toLocaleString('en-IN')}) of ₹{Number(selectedIncome.amount).toLocaleString('en-IN')}
                    </span>
                  )}
                </span>
              )}
            >
              <InputNumber
                placeholder="0.00"
                style={{ width: '100%' }}
                prefix="₹"
                min={0}
                precision={2}
                max={cropRemaining !== null && !isEdit ? cropRemaining : undefined}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Bank Account" name="bank_account_id" rules={[{ required: true, message: 'Select a bank account' }]}>
              <Select
                placeholder="Select bank account"
                showSearch
                suffixIcon={<BankOutlined />}
                filterOption={(input, opt) =>
                  opt?.value === ADD_BANK_OPTION || (opt?.label || '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={(val) => {
                  if (val === ADD_BANK_OPTION) {
                    form.setFieldValue('bank_account_id', watchedBankAccountId);
                    setQuickBankOpen(true);
                  }
                }}
                options={[
                  { value: 'cash', label: 'Cash' },
                  ...bankAccounts.map((b) => {
                    const dec = b.bank_details ? (decryptData(b.bank_details) || {}) : {};
                    const bankName = dec.bank_name || '';
                    const holder = dec.account_holder_name || '';
                    const accNum = dec.account_number ? `••${String(dec.account_number).slice(-4)}` : '';
                    const accName = b.name && b.name !== 'null' ? b.name : holder;
                    const parts = [accName, bankName, accNum].filter(Boolean);
                    const label = parts.length > 0 ? parts.join(' — ') : `Bank Account ${b.id}`;
                    return { value: b.id, label };
                  }),
                  {
                    value: ADD_BANK_OPTION,
                    label: <span style={{ color: PRIMARY, fontWeight: 600 }}><PlusOutlined /> Add New Bank Account</span>,
                  },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 4: Notes (full width) */}
        <Form.Item
          label="Notes (optional)"
          name="note"
          layout="horizontal"
          labelCol={{ flex: '0 0 auto' }}
          wrapperCol={{ flex: '1 1 auto' }}
        >
          <Input.TextArea rows={2} placeholder="Add a note..." />
        </Form.Item>
      </Form>

      <CategoryModal
        open={quickCategoryOpen}
        onClose={() => setQuickCategoryOpen(false)}
        onSuccess={(newId) => {
          onCategoryAdded?.();
          if (newId) form.setFieldValue('category_id', newId);
        }}
      />
      <QuickAddBankAccountModal
        open={quickBankOpen}
        onClose={() => setQuickBankOpen(false)}
        onSuccess={(newId) => {
          onBankAccountAdded?.();
          if (newId) form.setFieldValue('bank_account_id', newId);
        }}
      />
    </Modal>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function DailyExpensePage() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [incomeSources, setIncomeSources] = useState([]);
  const [loadingExp, setLoadingExp] = useState(false);
  const [loadingCat, setLoadingCat] = useState(false);
  const [activeTab, setActiveTab] = useState('expenses');

  // Server-side pagination
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

  // Summary from API
  const [summary, setSummary] = useState({ total_expense: 0, total_income: 0, balance: 0 });

  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [cropFilter, setCropFilter] = useState(null);
  const [bankFilter, setBankFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [titleFilter, setTitleFilter] = useState('');
  const [includePlanned, setIncludePlanned] = useState(false);
  const [sourceIncomeFilter, setSourceIncomeFilter] = useState(null);

  // Crops for filter dropdown
  const [filterCrops, setFilterCrops] = useState([]);
  const [loadingFilterCrops, setLoadingFilterCrops] = useState(false);

  // Expense modal
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [editExpense, setEditExpense] = useState(null);

  // Category modal
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);

  // Category search
  const [categorySearch, setCategorySearch] = useState('');

  // Debounce ref for title filter
  const debounceRef = React.useRef(null);

  // Guards against out-of-order responses: a large per_page (e.g. 3000) is
  // slow enough that firing "change page size" then quickly "go to page 2"
  // can have the first (now-stale) request resolve after the second,
  // clobbering the correct page with the wrong one. Only the response to
  // the most recently *fired* request is ever applied to state.
  const fetchIdRef = React.useRef(0);

  // ── Fetchers ──────────────────────────────────────────────────────────────
  const fetchExpenses = useCallback(async (page = 1, perPage = 15) => {
    const fetchId = ++fetchIdRef.current;
    setLoadingExp(true);
    try {
      const params = { page, per_page: perPage };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (categoryFilter) params.category_id = categoryFilter;
      if (cropFilter) params.crop_id = cropFilter;
      if (titleFilter) params.title = titleFilter;
      if (dateRange?.[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.end_date = dateRange[1].format('YYYY-MM-DD');
      if (includePlanned) params.include_planned = 1;
      if (sourceIncomeFilter) params.source_income_id = sourceIncomeFilter;
      if (bankFilter) params.bank_account_id = bankFilter;

      const res = await getDailyExpensesApi(params);
      if (fetchId !== fetchIdRef.current) return; // a newer request has since fired — ignore this stale response

      const raw = res.data?.data || [];
      setExpenses(Array.isArray(raw) ? raw : []);

      const pg = res.data?.pagination || {};
      setPagination((prev) => ({
        ...prev,
        current: pg.current_page ?? page,
        pageSize: pg.per_page ?? perPage,
        total: pg.total ?? 0,
      }));

      const sm = res.data?.summary || {};
      setSummary({
        total_expense: sm.total_expense ?? 0,
        total_income: sm.total_income ?? 0,
        balance: sm.balance ?? 0,
      });
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      message.error(apiErr(err, 'Failed to load expenses'));
    } finally {
      if (fetchId === fetchIdRef.current) setLoadingExp(false);
    }
  }, [typeFilter, categoryFilter, cropFilter, titleFilter, dateRange, includePlanned, sourceIncomeFilter, bankFilter]);

  const fetchCategories = useCallback(async () => {
    setLoadingCat(true);
    try {
      const res = await getDailyExpenseCategoriesApi();
      const raw = res.data?.data || res.data || [];
      setCategories(Array.isArray(raw) ? raw : []);
    } catch (err) {
      message.error(apiErr(err, 'Failed to load categories'));
    } finally {
      setLoadingCat(false);
    }
  }, []);

  const fetchBankAccounts = useCallback(async () => {
    try {
      const res = await getAccountsApi();
      const raw = res.data?.data || res.data || [];
      const all = Array.isArray(raw) ? raw : [];
      setBankAccounts(all.filter((a) => String(a.type).toLowerCase() === 'bank'));
    } catch {
      // bank accounts optional
    }
  }, []);

  const fetchIncomeSources = useCallback(async () => {
    try {
      const params = includePlanned ? { include_planned: 1 } : undefined;
      const res = await getIncomeSourcesApi(params);
      const raw = res.data?.data || [];
      setIncomeSources(Array.isArray(raw) ? raw : []);
    } catch {
      // optional
    }
  }, [includePlanned]);

  // Re-fetch from page 1 when filters change (debounce title)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchExpenses(1, pagination.pageSize), titleFilter ? 400 : 0);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, categoryFilter, cropFilter, titleFilter, dateRange, includePlanned, sourceIncomeFilter, bankFilter]);

  const fetchFilterCrops = useCallback(async () => {
    const currentYear = dayjs().year();
    const lang = localStorage.getItem('i18nextLng') || 'hi';
    setLoadingFilterCrops(true);
    try {
      const res = await getCropListFilterApi({ years: [String(currentYear), String(currentYear - 1)], lang, per_page: 200 });
      setFilterCrops(res.data?.data || []);
    } catch {
      // optional
    } finally {
      setLoadingFilterCrops(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchBankAccounts();
    fetchFilterCrops();
    fetchIncomeSources();
  }, [fetchCategories, fetchBankAccounts, fetchFilterCrops, fetchIncomeSources]);

  // ── Confirm (Mark as Done) handler ────────────────────────────────────────
  const handleConfirmExpense = async (id) => {
    try {
      await confirmDailyExpenseApi(id);
      message.success('Marked as done');
      fetchExpenses(pagination.current, pagination.pageSize);
      fetchIncomeSources();
      fetchBankAccounts();
    } catch (err) {
      message.error(apiErr(err, 'Failed to confirm entry'));
    }
  };

  // ── Delete handlers ───────────────────────────────────────────────────────
  const handleDeleteExpense = async (id) => {
    try {
      await deleteDailyExpenseApi(id);
      message.success('Entry deleted');
      fetchExpenses(pagination.current, pagination.pageSize);
      fetchIncomeSources();
    } catch (err) {
      message.error(apiErr(err, 'Failed to delete entry'));
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await deleteDailyExpenseCategoryApi(id);
      message.success('Category deleted');
      fetchCategories();
    } catch (err) {
      message.error(apiErr(err, 'Failed to delete category'));
    }
  };

  // ── Stats from API summary ────────────────────────────────────────────────
  const totalExpense = Number(summary.total_expense) || 0;
  const totalIncome = Number(summary.total_income) || 0;
  const balance = Number(summary.balance) || 0;

  // Crop net income for summary card
  const selectedCropForStats = cropFilter ? filterCrops.find((c) => c.id === cropFilter) : null;
  const cropNetIncome = selectedCropForStats
    ? Number(selectedCropForStats.vechan || 0) - Number(selectedCropForStats.kharch || 0)
    : null;
  const displayIncome = cropNetIncome !== null ? cropNetIncome : totalIncome;
  const displayBalance = cropNetIncome !== null ? cropNetIncome - totalExpense : balance;

  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const handlePrint = async (orientation = 'portrait') => {
    let allData = [];
    try {
      const params = { per_page: 9999 };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (categoryFilter) params.category_id = categoryFilter;
      if (cropFilter) params.crop_id = cropFilter;
      if (titleFilter) params.title = titleFilter;
      if (dateRange?.[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.end_date = dateRange[1].format('YYYY-MM-DD');
      if (includePlanned) params.include_planned = 1;
      if (sourceIncomeFilter) params.source_income_id = sourceIncomeFilter;
      if (bankFilter) params.bank_account_id = bankFilter;
      const res = await getDailyExpensesApi(params);
      const raw = res.data?.data || [];
      allData = Array.isArray(raw) ? raw : [];
    } catch {
      allData = expenses;
    }

    const printData = allData;

    const printExpense = printData.reduce((s, e) => e.type !== 'income' ? s + Number(e.amount) : s, 0);
    const printIncome = printData.reduce((s, e) => e.type === 'income' ? s + Number(e.amount) : s, 0);
    const printBalance = printIncome - printExpense;

    const win = window.open('', '_blank');
    const rows = printData
      .slice()
      .sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix())
      .map((e, idx) => {
        const cat = categories.find((c) => c.id === e.category_id);
        const catLabel = cat ? (cat.icon ? `${cat.icon} ${cat.name}` : cat.name) : '—';
        const sign = e.type === 'income' ? '+' : '−';
        const color = e.type === 'income' ? '#16a34a' : '#dc2626';

        let bankLabel = '—';
        if (e.bank_account_id === 'cash') {
          bankLabel = 'Cash';
        } else if (e.bank_details) {
          const dec = decryptData(e.bank_details) || {};
          const parts = [dec.account_holder_name, dec.bank_name].filter(Boolean);
          if (parts.length > 0) bankLabel = parts.join(' / ');
        }

        return `
          <tr>
            <td>${idx + 1}</td>
            <td class="col-date">${dayjs(e.date).format('DD MMM YYYY')}</td>
            <td class="col-category">${catLabel}</td>
            <td class="col-title">${e.title || '—'}</td>
            <td style="color:${color};font-weight:600;text-align:right">${sign}₹${Number(e.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            <td class="col-type">${e.type === 'income' ? 'Income' : 'Expense'}</td>
            <td class="col-bank" style="font-size:12px;color:#374151">${bankLabel}</td>
            <td class="col-note" style="color:#6b7280;font-size:12px">${e.note || ''}</td>
          </tr>`;
      })
      .join('');

    let userName = '';
    try { userName = JSON.parse(localStorage.getItem('user'))?.name || ''; } catch { }
    const logoUrl = `${window.location.origin}/logo.png`;

    win.document.write(`<!DOCTYPE html><html><head>
      <title>Daily Expense Report — Kheti Hisab</title>
      <style>
        @page { size: A4 ${orientation}; margin: 12mm; }
        * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: Arial, sans-serif; font-size: 13px; padding: 28px 32px; color: #111; }
        .brand { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
        .brand img { width: 40px; height: 40px; object-fit: contain; }
        .brand-name { font-size: 22px; font-weight: 700; color: #1a6b2a; letter-spacing: -0.3px; }
        .brand-tagline { font-size: 11px; color: #6b7280; margin-top: 1px; }
        .divider { border: none; border-top: 2px solid #1a6b2a; margin: 12px 0 16px; }
        .report-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 16px; }
        .report-title { font-size: 16px; font-weight: 700; color: #111; }
        .report-meta { font-size: 11px; color: #6b7280; text-align: right; line-height: 1.6; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        th { background: #1a6b2a; color: #fff; padding: 8px 10px; text-align: left; font-size: 12px; }
        td { padding: 7px 10px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
        /* Narrower fixed widths for Date/Category/Bank/Note — text still wraps
           in full (nothing is cut off), it just wraps within a narrower column
           instead of stretching wide, so more columns' worth fit per row. */
        th.col-date, td.col-date { width: 100px; }
        th.col-category, td.col-category { width: 140px; }
        th.col-title, td.col-title { width: 140px; }
        th.col-type, td.col-type { width: 80px; }
        th.col-bank, td.col-bank { width: 200px; }
        th.col-note, td.col-note { width: 140px; }
        td.col-date, td.col-category, td.col-title, td.col-type, td.col-bank, td.col-note { word-break: break-word; }
        tbody tr:nth-child(odd) td { background: #ffffff; }
        tbody tr:nth-child(even) td { background: #eef5ec; }
        .totals { margin-top: 16px; display: flex; gap: 40px; font-size: 13px; padding: 12px 14px; background: #f9fafb; border-radius: 6px; border: 1px solid #e5e7eb; }
        .totals span { font-weight: 700; }
        .footer { margin-top: 28px; text-align: center; font-size: 11px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 10px; }
        @media print { button { display: none; } }
      </style>
    </head><body>
      <div class="brand">
        <img src="${logoUrl}" alt="Kheti Hisab" onerror="this.style.display='none'" />
        <div>
          <div class="brand-name">Kheti Hisab</div>
          <div class="brand-tagline">Smart Farm Management</div>
        </div>
      </div>
      <hr class="divider" />
      <div class="report-header">
        <div class="report-title">Daily Expense Report</div>
        <div class="report-meta">
          ${userName ? `<div>Account: <strong>${userName}</strong></div>` : ''}
          <div>Printed: ${dayjs().format('DD MMM YYYY, hh:mm A')}</div>
          <div>Entries: ${printData.length}</div>
        </div>
      </div>
      <table>
        <thead><tr><th style="width:36px">Sr No.</th><th class="col-date">Date</th><th class="col-category">Category</th><th class="col-title">Title</th><th style="text-align:right">Amount</th><th class="col-type">Type</th><th class="col-bank">Bank</th><th class="col-note">Note</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="totals">
        <div>Total Expense: <span style="color:#dc2626">₹${printExpense.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
        <div>Total Income: <span style="color:#16a34a">₹${printIncome.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
        <div>Balance: <span style="color:${printBalance >= 0 ? '#1d4ed8' : '#dc2626'}">${printBalance < 0 ? '−' : ''}₹${Math.abs(printBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
      </div>
      <div class="footer">khethisab.shop &nbsp;·&nbsp; Kheti Hisab — Smart Farm Management</div>
      <script>window.onload = () => { window.print(); }<\/script>
    </body></html>`);
    win.document.close();
  };

  // ── Expense table columns ─────────────────────────────────────────────────
  const expenseColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      render: (v) => <Text style={{ fontSize: 13 }}>{formatDate(v)}</Text>,
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      defaultSortOrder: 'descend',
      width: 110,
    },
    {
      title: 'Category',
      dataIndex: 'category_id',
      render: (cid) => {
        const cat = categories.find((c) => c.id === cid);
        if (!cat) return <Text type="secondary">—</Text>;
        return (
          <Tag color="blue" style={{ fontSize: 12, fontWeight: 600 }}>
            {cat.icon ? `${cat.icon} ${cat.name}` : cat.name}
          </Tag>
        );
      },
      width: 140,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      render: (v, row) => (
        <Tooltip title={row.note || undefined}>
          <span style={{ fontWeight: 400 }}>{v}</span>
          {row.note && (
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 1, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.note}
            </div>
          )}
        </Tooltip>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (v, row) => (
        <span style={{ fontWeight: 700, fontSize: 14, color: row.type === 'income' ? '#52c41a' : '#ff4d4f' }}>
          {row.type === 'income' ? '+' : '−'}{formatCurrency(v)}
        </span>
      ),
      sorter: (a, b) => Number(a.amount) - Number(b.amount),
      width: 130,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      render: (v, row) => (
        <Space direction="vertical" size={2}>
          {v === 'income' ? (
            <Tag color="success" icon={<ArrowUpOutlined />}>Income</Tag>
          ) : (
            <Tag color="error" icon={<ArrowDownOutlined />}>Expense</Tag>
          )}
          {!!row.is_planned && (
            <Tag color="warning" icon={<ClockCircleOutlined />} style={{ fontSize: 11 }}>Upcoming</Tag>
          )}
        </Space>
      ),
      width: 110,
    },
    {
      title: 'Income Source',
      key: 'income',
      width: 160,
      render: (_, row) => {
        if (row.type === 'income') {
          const src = incomeSources.find((i) => i.id === row.id);
          if (!src) return <Text type="secondary">—</Text>;
          const remaining = Number(src.remaining);
          return (
            <span style={{ fontSize: 12, color: remaining <= 0 ? '#ff4d4f' : '#52c41a', fontWeight: 600 }}>
              ₹{remaining.toLocaleString('en-IN')} left
            </span>
          );
        }
        if (row.income_id) {
          return (
            <Tag color="geekblue" style={{ fontSize: 11 }}>
              From: {row.income_title || 'Income'}
            </Tag>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Bank',
      key: 'bank',
      width: 160,
      render: (_, row) => {
        if (!row.bank_details) return <Text type="secondary">—</Text>;
        const dec = decryptData(row.bank_details) || {};
        const bankName = dec.bank_name || '';
        const accName = dec.account_holder_name || '';
        if (!bankName && !accName) return <Text type="secondary">—</Text>;
        return (
          <div>
            {accName && <div style={{ fontSize: 12, fontWeight: 600 }}>{accName}</div>}
            {bankName && <div style={{ fontSize: 11, color: '#8c8c8c' }}>{bankName}</div>}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_, row) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          {!!row.is_planned && (
            <Tooltip title="Mark as done — confirms it as a real entry">
              <Popconfirm
                title="Mark as done?"
                description="This will now count toward your real balance."
                onConfirm={() => handleConfirmExpense(row.id)}
                okText="Mark Done"
              >
                <Button size="small" style={{ color: '#52c41a', borderColor: '#b7eb8f' }} icon={<CheckOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => { setEditExpense(row); setExpenseOpen(true); }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this entry?"
              description="This action cannot be undone."
              onConfirm={() => handleDeleteExpense(row.id)}
              okButtonProps={{ danger: true }}
              okText="Delete"
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── Category table columns ────────────────────────────────────────────────
  const categoryColumns = [
    {
      title: 'Icon',
      dataIndex: 'icon',
      width: 60,
      render: (v) => <span style={{ fontSize: 20 }}>{v || '📁'}</span>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title: 'Default Price',
      dataIndex: 'price',
      width: 130,
      render: (v) =>
        v ? (
          <span style={{ color: PRIMARY, fontWeight: 600 }}>{formatCurrency(v)}</span>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      render: (_, row) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => { setEditCategory(row); setCategoryOpen(true); }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this category?"
              description="Existing entries using this category will not be affected."
              onConfirm={() => handleDeleteCategory(row.id)}
              okButtonProps={{ danger: true }}
              okText="Delete"
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0, color: PRIMARY }}>
          <AccountBookOutlined style={{ marginRight: 8 }} />
          Daily Expense
        </Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => { fetchExpenses(pagination.current, pagination.pageSize); fetchCategories(); }}
            loading={loadingExp}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: PRIMARY, borderColor: PRIMARY }}
            onClick={() => { setEditExpense(null); setExpenseOpen(true); }}
          >
            Add Expense
          </Button>
        </Space>
      </div>

      {/* ── Summary Cards ─────────────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8}>
          <Card
            style={{ borderRadius: 10, border: '1px solid #ffccc7', background: '#fff2f0' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Statistic
              title={<Text style={{ color: '#ff4d4f', fontWeight: 600 }}>Total Expense</Text>}
              value={totalExpense}
              prefix="₹"
              precision={0}
              valueStyle={{ color: '#ff4d4f', fontWeight: 700 }}
              formatter={(v) => Number(v).toLocaleString('en-IN')}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            style={{ borderRadius: 10, border: '1px solid #b7eb8f', background: '#f6ffed' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Statistic
              title={<Text style={{ color: '#52c41a', fontWeight: 600 }}>Total Income</Text>}
              value={Math.abs(displayIncome)}
              prefix={displayIncome < 0 ? '−₹' : '₹'}
              precision={0}
              valueStyle={{ color: '#52c41a', fontWeight: 700 }}
              formatter={(v) => Number(v).toLocaleString('en-IN')}
            />
            {selectedCropForStats && (
              <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                🌱 {selectedCropForStats.crop_name || selectedCropForStats.crop || ''}
                {selectedCropForStats.verity ? ` · ${selectedCropForStats.verity}` : ''} net income
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            style={{
              borderRadius: 10,
              border: `1px solid ${displayBalance >= 0 ? '#91d5ff' : '#ffccc7'}`,
              background: displayBalance >= 0 ? '#e6f7ff' : '#fff2f0',
            }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Statistic
              title={<Text style={{ color: displayBalance >= 0 ? '#1677ff' : '#ff4d4f', fontWeight: 600 }}>Balance</Text>}
              value={Math.abs(displayBalance)}
              prefix={displayBalance < 0 ? '−₹' : '₹'}
              precision={0}
              valueStyle={{ color: displayBalance >= 0 ? '#1677ff' : '#ff4d4f', fontWeight: 700, fontSize: 22 }}
              formatter={(v) => Number(v).toLocaleString('en-IN')}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Include future transactions toggle ──────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        marginBottom: 20, padding: '8px 4px',
      }}>
        <Switch
          checked={includePlanned}
          onChange={setIncludePlanned}
          checkedChildren={<ClockCircleOutlined />}
        />
        <Text style={{ fontSize: 13, fontWeight: 500 }}>Include future transactions</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          — {includePlanned
            ? 'totals below include planned income/expenses that haven\'t happened yet'
            : 'planned entries are hidden from totals until confirmed'}
        </Text>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'expenses',
            label: (
              <Space>
                <DollarOutlined />
                {`Expenses${pagination.total ? ` (${pagination.total})` : ''}`}
              </Space>
            ),
          },
          {
            key: 'categories',
            label: (
              <Space>
                <TagOutlined />
                {`Categories${categories.length ? ` (${categories.length})` : ''}`}
              </Space>
            ),
          },
        ]}
      />

      {/* ── Expenses Tab ─────────────────────────────────────────────────── */}
      {activeTab === 'expenses' && (
        <Card style={{ borderRadius: 10 }}>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: 140 }}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'expense', label: '↓ Expense' },
                { value: 'income', label: '↑ Income' },
              ]}
            />
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              placeholder="All Categories"
              allowClear
              showSearch
              filterOption={(input, opt) =>
                (opt?.label || '').toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: 180 }}
              options={categories.map((c) => ({
                value: c.id,
                label: c.icon ? `${c.icon} ${c.name}` : c.name,
              }))}
            />
            <Input
              placeholder="Search title…"
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={titleFilter}
              onChange={(e) => setTitleFilter(e.target.value)}
              allowClear
              style={{ width: 180 }}
            />
            {/* Crop filter — temporarily disabled */}
            {/* <Select
              value={cropFilter}
              onChange={setCropFilter}
              placeholder="All Crops"
              allowClear
              showSearch
              loading={loadingFilterCrops}
              filterOption={(input, opt) =>
                (opt?.searchLabel || '').toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: 200 }}
              options={[
                ...[String(dayjs().year()), String(dayjs().year() - 1)].map((yr) => ({
                  label: <span style={{ fontWeight: 600, color: '#8c8c8c', fontSize: 12 }}>{yr}</span>,
                  title: yr,
                  options: filterCrops
                    .filter((c) => (c.crop_date || c.created_at || '').startsWith(yr))
                    .map((c) => ({
                      value: c.id,
                      label: `🌱 ${c.crop_name || c.crop || ''}${c.verity ? ` · ${c.verity}` : ''}`,
                      searchLabel: `${c.crop_name || c.crop || ''} ${c.verity || ''}`,
                    })),
                })).filter((g) => g.options.length > 0),
              ]}
            /> */}
            <Select
              value={bankFilter}
              onChange={setBankFilter}
              placeholder="All Banks"
              allowClear
              showSearch
              filterOption={(input, opt) =>
                (opt?.label || '').toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: 180 }}
              options={[
                { value: 'cash', label: 'Cash' },
                ...bankAccounts
                  .map((b) => {
                    const dec = b.bank_details ? (decryptData(b.bank_details) || {}) : {};
                    const bankName = dec.bank_name || '';
                    if (!bankName) return null;
                    const holder = dec.account_holder_name || '';
                    const label = [bankName, holder].filter(Boolean).join(' - ');
                    return { value: b.id, label };
                  })
                  .filter(Boolean),
              ]}
            />
            <Select
              value={sourceIncomeFilter}
              onChange={setSourceIncomeFilter}
              placeholder="Source spent (by income)"
              allowClear
              showSearch
              filterOption={(input, opt) => (opt?.searchLabel || '').toLowerCase().includes(input.toLowerCase())}
              style={{ width: 220 }}
              options={incomeSources.map((i) => ({
                value: i.id,
                label: `${i.is_planned ? '🕒 ' : ''}${i.title || 'Income'} — ₹${Number(i.amount).toLocaleString('en-IN')}`,
                searchLabel: i.title || '',
              }))}
            />
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="DD MMM YYYY"
              style={{ flex: 1, minWidth: 220 }}
              placeholder={['From date', 'To date']}
            />
            {(typeFilter !== 'all' || categoryFilter || cropFilter || bankFilter || titleFilter || dateRange || sourceIncomeFilter) && (
              <Button onClick={() => {
                setTypeFilter('all'); setCategoryFilter(null); setCropFilter(null);
                setBankFilter(null); setTitleFilter(''); setDateRange(null); setSourceIncomeFilter(null);
              }}>
                Clear Filters
              </Button>
            )}
            <Dropdown
              disabled={expenses.length === 0}
              menu={{
                items: [
                  { key: 'portrait', label: 'Print — Portrait' },
                  { key: 'landscape', label: 'Print — Landscape' },
                ],
                onClick: ({ key }) => handlePrint(key),
              }}
            >
              <Tooltip title="Print expense list">
                <Button icon={<PrinterOutlined />} disabled={expenses.length === 0} />
              </Tooltip>
            </Dropdown>
          </div>

          {loadingExp ? (
            <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
          ) : expenses.length === 0 ? (
            <Empty
              description={pagination.total === 0 ? 'No entries yet' : 'No entries match your filters'}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              {pagination.total === 0 && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{ background: PRIMARY, borderColor: PRIMARY }}
                  onClick={() => { setEditExpense(null); setExpenseOpen(true); }}
                >
                  Add First Entry
                </Button>
              )}
            </Empty>
          ) : (
            <Table
              dataSource={expenses}
              columns={expenseColumns}
              rowKey="id"
              loading={loadingExp}
              scroll={{ x: 680 }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: [15, 30, 50, 100, 200, 500, 2000, 3000, 10000],
                showTotal: (total) => `${total} entries`,
                onChange: (page, pageSize) => {
                  setPagination((p) => ({ ...p, current: page, pageSize }));
                  fetchExpenses(page, pageSize);
                },
              }}
            />
          )}
        </Card>
      )}

      {/* ── Categories Tab ────────────────────────────────────────────────── */}
      {activeTab === 'categories' && (
        <Card
          style={{ borderRadius: 10 }}
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              style={{ background: PRIMARY, borderColor: PRIMARY }}
              onClick={() => { setEditCategory(null); setCategoryOpen(true); }}
            >
              Add Category
            </Button>
          }
        >
          {loadingCat ? (
            <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
          ) : categories.length === 0 ? (
            <Empty
              description="No categories yet"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ background: PRIMARY, borderColor: PRIMARY }}
                onClick={() => { setEditCategory(null); setCategoryOpen(true); }}
              >
                Create First Category
              </Button>
            </Empty>
          ) : (
            <>
              <Input
                placeholder="Search categories…"
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                allowClear
                style={{ maxWidth: 280, marginBottom: 12 }}
              />
              <Table
                dataSource={filteredCategories}
                columns={categoryColumns}
                rowKey="id"
                // Categories are fetched in full, all at once (no server-side pagination —
                // see fetchCategories) specifically so there's no paginated experience here;
                // disable the Table's own pagination so it doesn't silently default to a
                // 10-per-page slice and hide categories past page 1.
                pagination={false}
                scroll={{ x: 400, y: 480 }}
                locale={{ emptyText: <Empty description="No matching categories" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
              />
            </>
          )}
        </Card>
      )}

      {/* ── Upgrade Tab ──────────────────────────────────────────────────── */}

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      <ExpenseFormModal
        open={expenseOpen}
        onClose={() => { setExpenseOpen(false); setEditExpense(null); }}
        onSuccess={() => { fetchExpenses(pagination.current, pagination.pageSize); fetchFilterCrops(); fetchIncomeSources(); }}
        editRecord={editExpense}
        categories={categories}
        bankAccounts={bankAccounts}
        incomeSources={incomeSources}
        onCategoryAdded={fetchCategories}
        onBankAccountAdded={fetchBankAccounts}
      />
      <CategoryModal
        open={categoryOpen}
        onClose={() => { setCategoryOpen(false); setEditCategory(null); }}
        onSuccess={fetchCategories}
        editRecord={editCategory}
      />
    </div>
  );
}
